// Process B — Denial Review closure (app.md §9). Invoices denied at any approval
// step land here. Read-only on invoice data. Deliberately NO bulk actions and NO
// row-level archive buttons (decided 2026-06-10): the reviewer must Open each
// invoice and read the rejector's comment in the decision history before
// archiving — the two closure buttons live only in the modal's action bar.
// Button language follows outline.md: "Archive for non-payment" / "Archive for
// fresh invoice". Both closures require a comment. Each writes a Denial Review
// decision, and the API moves the invoice to the matching terminal state.
import { useMemo, useState } from 'react'
import { Button, Body1, Toast, ToastTitle, makeStyles, tokens, useToastController } from '@fluentui/react-components'
import { ScreenTitle } from '../components/ScreenTitle'
import { ArchiveRegular, DocumentAddRegular } from '@fluentui/react-icons'
import type { Invoice } from '../lib/types'
import type { ReasonCategory } from '../lib/constants'
import { DecisionStep, DecisionOutcome, InvoiceStatus, TOASTER_ID } from '../lib/constants'
import { createDecision } from '../lib/data'
import { errorMessage } from '../api/errors'
import { canDecide, type Session } from '../lib/identity'
import { useQueue } from '../lib/hooks'
import { InvoiceQueue } from '../components/InvoiceQueue'
import { InvoiceDetailModal } from '../components/InvoiceDetailModal'
import { DecisionDialog } from '../components/DecisionDialog'
import { applyFilters, type InvoiceFilter } from '../lib/filters'
import { Loading, EmptyQueue, ErrorBanner } from '../components/States'

const useStyles = makeStyles({
  header: { display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' },
  sub: { color: tokens.colorNeutralForeground3 },
})

type CloseKind = 'no-payment' | 'fresh'
type Pending = { kind: CloseKind; id: string } | null

const NOT_AUTHORIZED =
  "You don't hold the Denial Review role. You can read this invoice, but your decision would be refused."

interface Props {
  /** The signed-in session — the archive buttons mirror the API's gate
      (canDecide at the Denial Review step), like every other decision surface. */
  session: Session
  refreshNonce?: number
  /** Page-wide filter (header Filter button; also drives the timeline badges). */
  filter: InvoiceFilter
  /** Called when acted rows leave the queue, so the timeline badges refresh too. */
  onQueuesChanged?: () => void
}

export function DenialReviewScreen({ session, refreshNonce, filter, onQueuesChanged }: Props) {
  const s = useStyles()
  const user = session.user
  const { invoices, loading, error, reload } = useQueue([InvoiceStatus.PendingDenialReview], refreshNonce)
  const filtered = useMemo(() => applyFilters(invoices, filter), [invoices, filter])

  const [openInvoice, setOpenInvoice] = useState<Invoice | null>(null)
  const [pending, setPending] = useState<Pending>(null)
  const [submitting, setSubmitting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const { dispatchToast } = useToastController(TOASTER_ID)

  const byId = useMemo(() => new Map(invoices.map((i) => [i.id, i])), [invoices])

  async function submit(id: string, kind: CloseKind, reason: string, category?: string) {
    // If a background reload dropped the row, there is nothing to archive — say
    // it's gone rather than reporting a success.
    const inv = byId.get(id)
    if (!inv) {
      setPending(null)
      setOpenInvoice(null)
      setActionError('That invoice is no longer in the Denial Review queue — it was likely handled elsewhere. Refresh to reload the queue.')
      return
    }
    // Mirror the API's gate — a decision made without Denial Review authority is
    // refused, so don't send it.
    if (!canDecide(session, DecisionStep.DenialReview, inv)) {
      setPending(null)
      setActionError(NOT_AUTHORIZED)
      return
    }
    setSubmitting(true)
    setActionError(null)
    const outcome = kind === 'no-payment' ? DecisionOutcome.ArchivedWithoutPayment : DecisionOutcome.ArchivedForFreshInvoice
    try {
      await createDecision({
        invoice_id: inv.id,
        step: DecisionStep.DenialReview,
        decision: outcome,
        reason,
        reason_category: (category as ReasonCategory) || undefined,
        row_version: inv.row_version,
      })
      setPending(null)
      setOpenInvoice(null)
      void reload()
      onQueuesChanged?.()
      dispatchToast(
        <Toast>
          <ToastTitle>
            Invoice archived{kind === 'no-payment' ? ' — no payment' : ' — awaiting a fresh invoice'}
          </ToastTitle>
        </Toast>,
        { intent: 'success' },
      )
    } catch (e) {
      setActionError(errorMessage(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <div className={s.header}>
        <ScreenTitle>Denial Review</ScreenTitle>
        <Body1 className={s.sub}>For each invoice, open it to review the denial comment, then archive for non-payment or archive for fresh invoice. A denied invoice cannot re-enter the approval chain.</Body1>
      </div>

      <ErrorBanner message={error || actionError} />

      {loading ? (
        <Loading label="Loading denied invoices…" />
      ) : filtered.length === 0 ? (
        <EmptyQueue text={invoices.length > 0 ? 'No invoices match the current filters.' : undefined} />
      ) : (
        <InvoiceQueue invoices={filtered} columns="amounts" onOpen={setOpenInvoice} />
      )}

      <InvoiceDetailModal
        open={!!openInvoice}
        invoice={openInvoice}
        showPdf
        user={user}
        onClose={() => setOpenInvoice(null)}
        actions={
          openInvoice &&
          (() => {
            const decidable = canDecide(session, DecisionStep.DenialReview, openInvoice)
            return (
              <>
                <Button
                  icon={<ArchiveRegular />}
                  disabled={submitting || !decidable}
                  title={decidable ? undefined : NOT_AUTHORIZED}
                  onClick={() => setPending({ kind: 'no-payment', id: openInvoice.id })}
                >
                  Archive for non-payment
                </Button>
                <Button
                  icon={<DocumentAddRegular />}
                  disabled={submitting || !decidable}
                  title={decidable ? undefined : NOT_AUTHORIZED}
                  onClick={() => setPending({ kind: 'fresh', id: openInvoice.id })}
                >
                  Archive for fresh invoice
                </Button>
              </>
            )
          })()
        }
      />

      <DecisionDialog
        open={!!pending}
        title={pending?.kind === 'no-payment' ? 'Archive this invoice for non-payment?' : 'Archive this invoice for a fresh invoice?'}
        body="This comment is recorded on the invoice. Archiving is terminal."
        requireReason
        reasonLabel="Reason"
        showReasonCategory
        confirmLabel="Archive"
        destructive
        submitting={submitting}
        onCancel={() => setPending(null)}
        onConfirm={(reason, category) => pending && submit(pending.id, pending.kind, reason, category)}
      />
    </div>
  )
}
